{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2024 - 2025                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.SideMenu;

{$modeswitch externalclass}

{$DEFINE NOPP}

interface

uses
  Classes, SysUtils, Types, WEBLib.Controls, WEBLib.StdCtrls, WebLib.ExtCtrls,
  WEBLib.Graphics, Web, JS, WEBLib.Menus, WEBLib.Forms, WEBLib.Dialogs;

type
  TSideMenuItemType = (itText, itCheckBox, itDivider, itControl, itHTML, itTitle);
  TSideMenuItemSelectionStyle = (itsFull, itsRounded);
  TSideMenuItemGlyphPosition = (itpLeft, itpRight);
  TSideMenuState = (msCollapsed, msExpanded, msPopup);
  TSideMenuStates = set of TSideMenuState;

  TSideMenuItems = class;
  TCustomSideMenu = class;

  TSideMenuAppearance = class(TPersistent)
  private
    FOwner: TCustomSideMenu;
    FOnChange: TNotifyEvent;
    FCaption: string;
    FCaptionColor: TColor;
    FCaptionVisible: Boolean;
    FFixedColor: TColor;
    FFixedVisible: Boolean;
    FFixedItemHoverColor: TColor;
    FItemHoverColor: TColor;
    FFixedHeight: Integer;
    FCaptionPopup: string;
    FFixedItemSelectedTextColor: TColor;
    FFixedItemSelectedColor: TColor;
    FItemSelectedTextColor: TColor;
    FFixedItemHoverTextColor: TColor;
    FItemSelectedColor: TColor;
    FItemHoverTextColor: TColor;
    FCaptionFont: TFont;
    FItemFont: TFont;
    FFixedItemFont: TFont;
    FItemSelectionStyle: TSideMenuItemSelectionStyle;
    FItemExpandedMaterialGlyph: TMaterialGlyph;
    FItemExpandGlyphPosition: TSideMenuItemGlyphPosition;
    FItemCollapsedMaterialGlyph: TMaterialGlyph;
    FStates: TSideMenuStates;
    FOnCSSUpdate: TNotifyEvent;
    procedure SetCaption(const Value: string);
    procedure SetCaptionColor(const Value: TColor);
    procedure SetCaptionPopup(const Value: string);
    procedure SetCaptionVisible(const Value: Boolean);
    procedure SetFixedColor(const Value: TColor);
    procedure SetFixedHeight(const Value: Integer);
    procedure SetFixedItemHoverColor(const Value: TColor);
    procedure SetFixedVisible(const Value: Boolean);
    procedure SetItemHoverColor(const Value: TColor);
    procedure SetFixedItemHoverTextColor(const Value: TColor);
    procedure SetFixedItemSelectedColor(const Value: TColor);
    procedure SetFixedItemSelectedTextColor(const Value: TColor);
    procedure SetItemHoverTextColor(const Value: TColor);
    procedure SetItemSelectedColor(const Value: TColor);
    procedure SetItemSelectedTextColor(const Value: TColor);
    procedure SetItemSelectionStyle(const Value: TSideMenuItemSelectionStyle);
    procedure SetItemCollapsedMaterialGlyph(const Value: TMaterialGlyph);
    procedure SetItemExpandedMaterialGlyph(const Value: TMaterialGlyph);
    procedure SetItemExpandGlyphPosition(
      const Value: TSideMenuItemGlyphPosition);
    procedure SetItemFont(const Value: TFont);
    procedure SetCaptionFont(const Value: TFont);
    procedure SetFixedItemFont(const Value: TFont);
    procedure SetStates(const Value: TSideMenuStates);
  protected
    procedure DoChange; virtual;
    procedure DoCSS; virtual;
    procedure DoFontChange(Sender: TObject);
    property ItemExpandGlyphPosition: TSideMenuItemGlyphPosition read FItemExpandGlyphPosition write SetItemExpandGlyphPosition default itpLeft;
  public
    constructor Create(AOwner: TCustomSideMenu);
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
    property OnCSSUpdate: TNotifyEvent read FOnCSSUpdate write FOnCSSUpdate;
  published
    property Caption: string read FCaption write SetCaption;
    property CaptionColor: TColor read FCaptionColor write SetCaptionColor default clWebSkyBlue;
    property CaptionPopup: string read FCaptionPopup write SetCaptionPopup;
    property CaptionVisible: Boolean read FCaptionVisible write SetCaptionVisible default True;
    property CaptionFont: TFont read FCaptionFont write SetCaptionFont;

    property ItemFont: TFont read FItemFont write SetItemFont;
    property ItemHoverColor: TColor read FItemHoverColor write SetItemHoverColor default clWebSilver;
    property ItemSelectedColor: TColor read FItemSelectedColor write SetItemSelectedColor default clWebSkyBlue;
    property ItemHoverTextColor: TColor read FItemHoverTextColor write SetItemHoverTextColor default clWebBlack;
    property ItemSelectedTextColor: TColor read FItemSelectedTextColor write SetItemSelectedTextColor default clWebBlack;
    property ItemSelectionStyle: TSideMenuItemSelectionStyle read FItemSelectionStyle write SetItemSelectionStyle default itsFull;
    property ItemCollapsedMaterialGlyph: TMaterialGlyph read FItemCollapsedMaterialGlyph write SetItemCollapsedMaterialGlyph;
    property ItemExpandedMaterialGlyph: TMaterialGlyph read FItemExpandedMaterialGlyph write SetItemExpandedMaterialGlyph;

    property FixedColor: TColor read FFixedColor write SetFixedColor default clWebWhiteSmoke;
    property FixedHeight: Integer read FFixedHeight write SetFixedHeight default 200;
    property FixedVisible: Boolean read FFixedVisible write SetFixedVisible default True;

    property FixedItemFont: TFont read FFixedItemFont write SetFixedItemFont;
    property FixedItemHoverColor: TColor read FFixedItemHoverColor write SetFixedItemHoverColor default clWebSilver;
    property FixedItemSelectedColor: TColor read FFixedItemSelectedColor write SetFixedItemSelectedColor default clWebSkyBlue;
    property FixedItemHoverTextColor: TColor read FFixedItemHoverTextColor write SetFixedItemHoverTextColor default clWebBlack;
    property FixedItemSelectedTextColor: TColor read FFixedItemSelectedTextColor write SetFixedItemSelectedTextColor default clWebBlack;

    property States: TSideMenuStates read FStates write SetStates default [msCollapsed, msExpanded, msPopup];
  end;

  TSideMenuItem = class(TCollectionItem)
  private
    FOwner: TCustomSideMenu;
    FText: string;
    FEnabled: Boolean;
    FOnClick: TNotifyEvent;
    FHint: string;
    FContentControl: TCustomControl;
    FImageURLCollapsed: string;
    FFormClass: TWebFormClass;
    FImageURL: string;
    FType: TSideMenuItemType;
    FChecked: Boolean;
    FHTML: TStringList;
    FControl: TControl;
    FSubItem: Boolean;
    FExpanded: Boolean;
    FMaterialGlyph: TMaterialGlyph;
    FMaterialGlyphType: TMaterialGlyphType;
    FBadgeText: string;
    FBadgeColor: TColor;
    FBadgeTextColor: TColor;
    procedure SetText(const Value: string);
    procedure SetEnabled(const Value: Boolean);
    procedure SetHint(const Value: string);
    procedure SetContentControl(const Value: TCustomControl);
    procedure SetImageURLCollapsed(const Value: string);
    procedure SetImageURL(const Value: string);
    procedure SetType(const Value: TSideMenuItemType);
    procedure SetChecked(const Value: Boolean);
    procedure SetHTML(const Value: TStringList);
    procedure SetControl(const Value: TControl);
    procedure SetSubItem(const Value: Boolean);
    procedure SetExpanded(const Value: Boolean);
    procedure SetMaterialGlyph(const Value: TMaterialGlyph);
    procedure SetMaterialGlyphType(const Value: TMaterialGlyphType);
    procedure SetBadgeText(const Value: string);
    procedure SetBadgeColor(const Value: TColor);
    procedure SetBadgeTextColor(const Value: TColor);
  protected
    procedure DoChanged;
    procedure Click;
  public
    constructor Create(Collection: TCollection);  override;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    property FormClass: TWebFormClass read FFormClass write FFormClass;
  published
    property ContentControl: TCustomControl read FContentControl write SetContentControl;
    property Enabled: Boolean read FEnabled write SetEnabled default True;
    property Hint: string read FHint write SetHint;
    property ImageURLCollapsed: string read FImageURLCollapsed write SetImageURLCollapsed;
    property ImageURL: string read FImageURL write SetImageURL;
    property Text: string read FText write SetText;
    property &Type: TSideMenuItemType read FType write SetType;
    property Checked: Boolean read FChecked write SetChecked default False;
    property Expanded: Boolean read FExpanded write SetExpanded default False;
    property SubItem: Boolean read FSubItem write SetSubItem default False;
    property HTML: TStringList read FHTML write SetHTML;
    property Control: TControl read FControl write SetControl;
    property MaterialGlyph: TMaterialGlyph read FMaterialGlyph write SetMaterialGlyph;
    property MaterialGlyphType: TMaterialGlyphType read FMaterialGlyphType write SetMaterialGlyphType default mgNormal;
    property BadgeText: string read FBadgeText write SetBadgeText;
    property BadgeColor: TColor read FBadgeColor write SetBadgeColor default clRed;
    property BadgeTextColor: TColor read FBadgeTextColor write SetBadgeTextColor default clWhite;

    property OnClick: TNotifyEvent read FOnClick write FOnClick;
  end;

  TNormalSideMenuItem = class(TSideMenuItem)
  end;

  TFixedSideMenuItem = class(TSideMenuItem)
  end;

  TSideMenuItems = class(TOwnedCollection)
  private
    FOwner: TCustomSideMenu;
    FOnChange: TNotifyEvent;
    function GetItems(AIndex: integer): TSideMenuItem;
    procedure SetItems(AIndex: integer; const Value: TSideMenuItem);
  protected
    procedure Update(Item: TCollectionItem); override;
    procedure DoChanged; virtual;
  public
    constructor Create(AOwner: TPersistent; AItemClass: TCollectionItemClass); reintroduce;
    function Add: TSideMenuItem; reintroduce;
    function Insert(AIndex: integer): TSideMenuItem; reintroduce;
    property Items[AIndex: integer]: TSideMenuItem read GetItems write SetItems; default;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  end;

  TSideMenuItemClickEvent = procedure(Sender: TObject; AIndex: integer) of object;

  TCustomSideMenu = class(TWebCustomControl)
  private
    FAutoSize: Boolean;
    FIsSizing: Boolean;
    FPadding: TPadding;
    FPanelBody: TJSHTMLElement;
    FElementBodyClassName: TElementClassName;
    FAlignment: TAlignment;
    FContentControl: TCustomControl;
    FContentForm: TWebForm;
    FItems: TSideMenuItems;
    FFixedItems: TSideMenuItems;
    FOnFixedItemClick: TSideMenuItemClickEvent;
    FOnItemClick: TSideMenuItemClickEvent;
    FElementItemsClassName: TElementClassName;
    FElementFixedItemsClassName: TElementClassName;
    FElementCaptionClassName: TElementClassName;
    FElementContentClassName: TElementClassName;
    FElementCaptionPopupClassName: TElementClassName;
    FContent: TStringList;
//    FParentControl: TCustomControl;
    FAppearance: TSideMenuAppearance;
    FSelectedItem: Integer;
    FItemsScrollTop: Integer;
    FFixedItemsScrollTop: Integer;
    procedure SetPadding(const Value: TPadding);
    procedure SetAlignment(const Value: TAlignment);
    procedure SetContentControl(const Value: TCustomControl);
    procedure SetContentText;
    procedure SetItems(const Value: TSideMenuItems);
    procedure SetFixedItems(const Value: TSideMenuItems);
    function HandleItemClick(Event: TJSEvent): Boolean; virtual;
    function HandleExpandCollapseClick(Event: TJSEvent): Boolean; virtual;
    function HandleFixedItemClick(Event: TJSEvent): Boolean; virtual;
    function HandleItemMouseEnter(Event: TJSEvent): Boolean; virtual;
    function HandleItemMouseLeave(Event: TJSEvent): Boolean; virtual;
    function HandleFixedItemMouseEnter(Event: TJSEvent): Boolean; virtual;
    function HandleFixedItemMouseLeave(Event: TJSEvent): Boolean; virtual;
    function GetContentElementID: string;
    procedure SetContent(const Value: TStringList);
//    procedure SetParentControl(const Value: TCustomControl);
    procedure SetAppearance(const Value: TSideMenuAppearance);
    procedure SetSelectedItem(const Value: Integer);
    procedure UpdateContent(AContentControl: TCustomControl; AFormClass: TWebFormClass);
  protected
    function CreateElement: TJSElement; override;
    procedure SetBorderStyle(const AValue: TBorderStyle); override;
    procedure SetAutoSize(AValue: Boolean);
    procedure UpdateElementVisual; override;
    procedure UpdateParent; override;
    procedure DoUpdateList;
    procedure Loaded; override;
    procedure DoContentChanged(Sender: TObject);
    procedure DoAppearanceChanged(Sender: TObject);
    procedure DoCSSUpdate(Sender: TObject);

    procedure ItemClick(AIndex: Integer); virtual;
    procedure FixedItemClick(AIndex: Integer); virtual;

    function GetOuterWidth: Integer; override;
    function GetOuterHeight: Integer; override;
    function GetChildContainer: TJSElement; override;
    procedure RegisterParent(AValue: TControl); override;
    procedure UnRegisterParent(AValue: TControl); override;
    procedure UpdatePadding(var ARect: TRect); override;
    procedure DoAutoSize;
    procedure UpdateCSS;
    procedure AlignControl(AControl: TControl); override;
    property AutoSize: Boolean read FAutoSize write SetAutoSize default False;
    property Alignment: TAlignment read FAlignment write SetAlignment default taCenter;
    property FixedItems: TSideMenuItems read FFixedItems write SetFixedItems;
    property ElementBodyClassName: TElementClassName read FElementBodyClassName write FElementBodyClassName;
    property ElementFixedItemsClassName: TElementClassName read FElementFixedItemsClassName write FElementFixedItemsClassName;
    property ElementCaptionClassName: TElementClassName read FElementCaptionClassName write FElementCaptionClassName;
    property ElementCaptionPopupClassName: TElementClassName read FElementCaptionPopupClassName write FElementCaptionPopupClassName;
    property ElementContentClassName: TElementClassName read FElementContentClassName write FElementContentClassName;
    property ElementItemsClassName: TElementClassName read FElementItemsClassName write FElementItemsClassName;
    property Items: TSideMenuItems read FItems write SetItems;
    property Padding: TPadding read FPadding write SetPadding;
    property Content: TStringList read FContent write SetContent;
    property SelectedItem: Integer read FSelectedItem write SetSelectedItem default -1;
  public
    procedure CreateInitialize; override;
    destructor Destroy; override;
    procedure EndUpdate; override;
    procedure SetBounds(X, Y, AWidth, AHeight: Integer); override;
    procedure Open;
    procedure Close;

    property ContentControl: TCustomControl read FContentControl write SetContentControl;
    property ContentElementID: string read GetContentElementID;
//    property ParentControl: TCustomControl read FParentControl write SetParentControl;
    property Appearance: TSideMenuAppearance read FAppearance write SetAppearance;

    property OnItemClick: TSideMenuItemClickEvent read FOnItemClick write FOnItemClick;
    property OnFixedItemClick: TSideMenuItemClickEvent read FOnFixedItemClick write FOnFixedItemClick;
  end;

  TSideMenu = class(TCustomSideMenu)
  published
    property Align;
    property Alignment;
    property AlignWithMargins;
    property Anchors;
    property Appearance;
//    property AutoSize;
//    property BorderColor;
//    property BorderStyle;
    property Content;
    property ChildOrder;
    property Color;
    property DragMode;
    property ElementBodyClassName;
    property ElementFixedItemsClassName;
    property ElementCaptionClassName;
    property ElementCaptionPopupClassName;
    property ElementContentClassName;
    property ElementItemsClassName;
    property ElementID;
    property ElementFont;
//    property ElementPosition;
    property HeightPercent;
    property HeightStyle default ssAuto;
    property Margins;
    property Items;
    property FixedItems;
    property Padding;
    property ParentFont;
    property PopupMenu;
    property Role;
    property TabOrder;
    property TabStop;
    property WidthPercent;
    property WidthStyle default ssAuto;
    property SelectedItem;

    property OnClick;
    property OnDblClick;
    property OnDragDrop;
    property OnDragOver;

    property OnItemClick;
    property OnFixedItemClick;

    property OnEndDrag;
    property OnStartDrag;
    property OnTouchStart;
    property OnTouchMove;
    property OnTouchEnd;
    property OnTouchCancel;
  end;

  TWebSideMenu = class(TSideMenu);

implementation

uses
  WEBLib.Utils;

{ TCustomSideMenu }

procedure TCustomSideMenu.CreateInitialize;
begin
  inherited;
  if (csDesigning in ComponentState) then
    EnablePropagation := True;
  AutoSize := True;
  WidthStyle := ssAuto;
  HeightStyle := ssAuto;
  ElementPosition := epRelative;
  BorderColor := clGray;

  FAlignment := taCenter;
  Color := clBtnFace;
  BorderStyle := bsNone;
  TabStop := False;
  CustomBorder := True;
  ControlStyle := ControlStyle + [csAcceptsControls];
  FPadding := TPadding.Create;
  FContentControl := nil;
//  FParentControl := nil;
  FContentForm := nil;
  FSelectedItem := -1;
  FItemsScrollTop := 0;
  FFixedItemsScrollTop := 0;
  FItems := TSideMenuItems.Create(Self, TNormalSideMenuItem);
  FItems.OnChange := DoAppearanceChanged;
  FFixedItems := TSideMenuItems.Create(Self, TFixedSideMenuItem);
  FFixedItems.OnChange := DoAppearanceChanged;

  FContent := TStringList.Create;
  FContent.OnChange := DoContentChanged;

  FAppearance := TSideMenuAppearance.Create(Self);
  FAppearance.OnChange := DoAppearanceChanged;
  FAppearance.OnCSSUpdate := DoCSSUpdate;

  if (csDesigning in ComponentState) then
  begin
    Width := 400;
    Height := 300;
  end;

  if DesignTime then
  begin
    FItems.Add.Text := 'Item 1';
    FItems.Add.Text := 'Item 2';
    FItems.Add.Text := 'Item 3';
  end;
end;

destructor TCustomSideMenu.Destroy;
begin
  FPadding.Free;
  FItems.Free;
  FFixedItems.Free;
  FContent.Free;
  FAppearance.Free;
  inherited;
end;

procedure TCustomSideMenu.EndUpdate;
begin
  inherited;
  if AutoSize then
    AutoSize := true;
end;

function TCustomSideMenu.GetChildContainer: TJSElement;
begin
  if Assigned(FPanelBody) then
    Result := FPanelBody
  else
    Result := inherited;
end;

function TCustomSideMenu.GetContentElementID: string;
begin
  Result := GetID + 'content';
end;

function TCustomSideMenu.GetOuterHeight: integer;
begin
  Result := inherited GetOuterHeight;
end;

function TCustomSideMenu.GetOuterWidth: integer;
begin
  Result := inherited GetOuterWidth;
end;

function TCustomSideMenu.HandleItemClick(Event: TJSEvent): Boolean;
var
  h, el: TJSHTMLElement;
  it: TSideMenuItem;
  v: string;
  idx, err: integer;
begin
  Result := True;

  el := TJSHTMLElement(document.getElementById(GetID + 'nrm'));
  if Assigned(el) then
  begin
    FItemsScrollTop := el.scrollTop;
  end;

  h := TJSHTMLElement(Event.target);
  v := h.getAttribute('data');
  if not Assigned(v) then
    v := h.parentElement.getAttribute('data');

  val(v, idx, err);

  if (err = 0) and (idx >= 0) and (idx < Items.Count) then
  begin
    Close;
    ItemClick(idx);

    if (SelectedItem >= 0) and (SelectedItem < Items.Count) then
    begin
      h := TJSHTMLElement(document.getElementById(GetID + IntToStr(SelectedItem)));
      h.style.setProperty('background-color', 'unset');
      h.style.setProperty('color', 'unset');
    end;
    SelectedItem := idx;
    h := TJSHTMLElement(document.getElementById(GetID + IntToStr(SelectedItem)));
    h.style.setProperty('background-color', ColorToHTML(Appearance.ItemSelectedColor));
    h.style.setProperty('color', ColorToHTML(Appearance.ItemSelectedTextColor));

    it := Items[idx];
    if it.&Type = itCheckBox then
      it.Checked := not it.Checked;
    it.Expanded := not it.Expanded;
    it.Click;
  end;
end;

function TCustomSideMenu.HandleItemMouseLeave(Event: TJSEvent): Boolean;
var
  h: TJSHTMLElement;
begin
  Result := True;

  h := TJSHTMLElement(Event.target);
  if h.id = GetID + IntToStr(SelectedItem) then
  begin
    h.style.setProperty('background-color', ColorToHTML(Appearance.ItemSelectedColor));
    h.style.setProperty('color', ColorToHTML(Appearance.ItemSelectedTextColor));
  end
  else
  begin
    h.style.setProperty('background-color', 'unset');
    h.style.setProperty('color', 'unset');
  end;
end;

function TCustomSideMenu.HandleItemMouseEnter(Event: TJSEvent): Boolean;
var
  h: TJSHTMLElement;
begin
  Result := True;

  h := TJSHTMLElement(Event.target);
  h.style.setProperty('background-color', ColorToHTML(Appearance.ItemHoverColor));
  h.style.setProperty('color', ColorToHTML(Appearance.ItemHoverTextColor));
end;

function TCustomSideMenu.HandleFixedItemMouseLeave(Event: TJSEvent): Boolean;
var
  h: TJSHTMLElement;
begin
  Result := True;

  h := TJSHTMLElement(Event.target);
  h.style.setProperty('background-color', 'unset');
  h.style.setProperty('color', 'unset');
end;

function TCustomSideMenu.HandleFixedItemMouseEnter(Event: TJSEvent): Boolean;
var
  h: TJSHTMLElement;
begin
  Result := True;

  h := TJSHTMLElement(Event.target);
  h.style.setProperty('background-color', ColorToHTML(Appearance.FixedItemHoverColor));
  h.style.setProperty('color', ColorToHTML(Appearance.FixedItemHoverTextColor));
end;

procedure TCustomSideMenu.Loaded;
begin
  inherited;
  DoUpdateList;
end;

procedure TCustomSideMenu.Open;
var
  el: TJSElement;
begin
  el := document.getElementById(GetID + 'sidebar');
  if Assigned(el) then
    el.classList.add('sidebar-open');
end;

procedure TCustomSideMenu.ItemClick(AIndex: integer);
begin
  if Assigned(OnItemClick) then
    OnItemClick(Self, AIndex);
end;

function TCustomSideMenu.HandleExpandCollapseClick(Event: TJSEvent): Boolean;
var
  h, el: TJSHTMLElement;
  it: TSideMenuItem;
  v: string;
  idx, err: integer;
begin
  Result := True;

  el := TJSHTMLElement(document.getElementById(GetID + 'nrm'));
  if Assigned(el) then
  begin
    FItemsScrollTop := el.scrollTop;
  end;

  el := TJSHTMLElement(document.getElementById(GetID + 'btm'));
  if Assigned(el) then
  begin
    FFixedItemsScrollTop := el.scrollTop;
  end;

  Event.stopPropagation();

  h := TJSHTMLElement(Event.target);
  v := h.getAttribute('data');
  if not Assigned(v) then
    v := h.parentElement.getAttribute('data');

  val(v, idx, err);

  if (err = 0) and (idx >= 0) and (idx < Items.Count) then
  begin
//    Close;
    ItemClick(idx);
    it := Items[idx];
    it.Expanded := not it.Expanded;
    it.Click;
  end;
end;

function TCustomSideMenu.HandleFixedItemClick(Event: TJSEvent): Boolean;
var
  h, el: TJSHTMLElement;
  it: TSideMenuItem;
  v: string;
  idx, err: integer;
begin
  Result := True;

  el := TJSHTMLElement(document.getElementById(GetID + 'btm'));
  if Assigned(el) then
  begin
    FFixedItemsScrollTop := el.scrollTop;
  end;

  h := TJSHTMLElement(Event.target);
  v := h.getAttribute('data');
  //console.log('fixed item click: ' + h.innerHTML,h,v);

  val(v, idx, err);

  if (err = 0) and (idx >= 0) and (idx < FixedItems.Count) then
  begin
    Close;
    FixedItemClick(idx);
    it := FixedItems[idx];
    it.Checked := not it.Checked;
    it.Click;
  end;
end;

procedure TCustomSideMenu.RegisterParent(AValue: TControl);
begin
  inherited;
  if AutoSize then
    DoAutoSize;
end;

procedure TCustomSideMenu.AlignControl(AControl: TControl);
begin
  inherited;
  if AutoSize then
    DoAutoSize;
end;

procedure TCustomSideMenu.FixedItemClick(AIndex: integer);
begin
  if Assigned(OnFixedItemClick) then
    OnFixedItemClick(Self, AIndex);
end;

procedure TCustomSideMenu.Close;
var
  el: TJSElement;
begin
  el := document.getElementById(GetID + 'sidebar');
  if Assigned(el) then
    el.classList.remove('sidebar-open');
end;

procedure TCustomSideMenu.UpdateCSS;
var
  css: string;
begin
  //console.log(Appearance.FixedHeight);

  css := css +
	':root {'+
	'  --white: white;'+
	'  --smoke: whitesmoke;'+
	'  --silver: silver;'+
	'  --light: lightskyblue;'+
	'  --gray: gray;'+
	'}'+

	'body'+
	'{'+
	'	 margin: 0;'+
	'}'+

  '.item-image'+
  '{' +
  '  margin-right: 10px;'+
  '  width: 16px;'+
  '  height: 16px;'+
  '}'+

  '.item-image-large'+
  '{' +
  '  padding-right: 5px;'+
  '  padding-left: 5px;'+
  '  width: 32px;'+
  '  height: 32px;'+
  '}'+

  '.item-badge'+
  '{' +
  '  margin-left: 5px;'+
  '  padding: 1% 4%;'+
  '  border-radius: 50%;'+
  '}'+

  '.item-check'+
  '{' +
  '  margin-right: 8px;'+
  '}'+

  '.material-icons,'+
  '.material-icons-outlined,'+
  '.material-icons-round,'+
  '.material-icons-sharp'+
  '{' +
  '  margin-right: 8px;'+
  '}'+

	'.smcontainer'+
	'{'+
	'	 display: grid;'+
	'	 transition: all 0.5s ease;'+
	'}'+

	'.smcontainer-open'+
	'{'+
	'	 grid-template-columns: 250px 1fr;'+
	'}'+

  '.smcontainer-open .item-image'+
  '{' +
  '  display: inline;' +
  '}' +

  '.smcontainer-open .item-image-large'+
  '{' +
  '  display: none;' +
  '}' +

  '.smcontainer-open .item-control-glyph'+
  '{' +
  '  display: none;' +
  '}' +

  '.smcontainer-open .item-text'+
  '{' +
  '  display: inline;' +
  '}' +

	'.smcontainer-open .item-expand-arrow'+
	'{'+
	'	 margin-right: 0;'+
	'}'+

  '.smcontainer-open .header-icon-close'+
  '{' +
  '  display: none;' +
  '}' +

	'.smcontainer-close'+
	'{'+
	'	 grid-template-columns: 65px 1fr;'+
	'}'+

  '.smcontainer-close .item-image'+
  '{' +
  '  display: none;' +
  '}' +

  '.smcontainer-close .item-image-large'+
  '{' +
  '  display: inline;' +
  '}' +

  '.smcontainer-close .item-control-glyph'+
  '{' +
  '  display: inline;' +
  '}' +

	'.smcontainer-close .item-badge'+
  '{' +
  '  display: none;' +
  '}' +

	'.smcontainer-close .menu-subitem'+
	'{'+
	'	 padding-left: 8px;'+
	'}'+

	'.smcontainer-close .material-icons,'+
	'.smcontainer-close .material-icons-outlined,'+
	'.smcontainer-close .material-icons-rounded,'+
	'.smcontainer-close .material-icons-sharp'+
	'{'+
	'	 margin-left: 8px;'+
	'}'+

	'.smcontainer-close .item-expand'+
	'{'+
  '  position: relative;'+
  '  left: -16px;'+
	'	 margin-left: 0;'+
	'	 margin-right: 0;'+
	'}'+

	'.smcontainer-close .item-expand-arrow'+
	'{'+
  '  position: relative;'+
  '  left: -10px;'+
	'	 margin-left: 0;'+
	'	 margin-right: 0;'+
	'}'+

  '.smcontainer-close .item-text'+
  '{'+
  '  display: none;' +
  '}'+

  '.smcontainer-close .header-text'+
  '{'+
  '  visibility: hidden;'+
  '}'+

  '.smcontainer-close .header-icon-open'+
  '{'+
  '  display: none;'+
  '}'+

	'.sidebar'+
	'{'+
	'	 position: sticky;'+
	'	 top: 0;'+
	'	 height: 100vh;'+
	'	 transition: width 0.5s ease;'+
	'	 transition: left 0.5s ease;'+
	'	 display: flex;'+
	'	 overflow-x: hidden;'+
	'	 overflow-y: hidden;'+
  '  z-index: 9999;'+
	'}'+

	'.sidebar-items'+
	'{'+
	'	 width: 100%;'+
	'	 display: flex;'+
  '  flex-flow: column;'+
  '  height: 100%;'+
	'	 text-wrap: nowrap;'+
	'}'+

	'.menu-item'+
	'{'+
	'	 padding: 8px;'+
  '  cursor: pointer;'+
  '  overflow: hidden;'+
	'}'+

	'.menu-item-rounded'+
	'{'+
	'	 margin: 5px;'+
  '  border-radius: 5px;'+
	'}'+

	'.menu-subitem'+
	'{'+
	'	 padding-left: 31px;'+
	'}'+

	'.menu-item-divider'+
	'{'+
	'	 padding: 0;'+
  '  height: 1px;'+
  '  width: 100%;'+
	'}'+

  '.item-title'+
  '{'+
  '  cursor: default;'+
  '}'+

  '.item-disabled'+
  '{'+
  '  color: var(--silver)!important;'+
  '  cursor: default;'+
  '}'+

  '.item-disabled img'+
  '{'+
  '  filter: grayscale(1);'+
  '}'+

	'.items-top'+
	'{'+
	'	 overflow-x: hidden;'+
	'	 overflow-y: auto;'+
  '  flex: 1 1 auto;'+
	'}'+

	'.items-bottom'+
	'{'+
  '  height: ' + Appearance.FixedHeight.ToString + 'px;'+
	'	 overflow-x: hidden;'+
  '  overflow-y: auto;'+
	'}'+

	'@supports not selector(::-webkit-scrollbar){'+
	'  .items-top,'+
	'  .items-bottom'+
	'  {'+
	'    scrollbar-width: thin;'+
	'  }'+
	'}'+

	'.items-top::-webkit-scrollbar,'+
	'.items-bottom::-webkit-scrollbar'+
  '{'+
  '  width: 4px;'+
	'}'+

	'.items-top::-webkit-scrollbar-thumb,'+
	'.items-bottom::-webkit-scrollbar-thumb'+
  '{'+
  '  background: silver;'+
  '  border-radius: 4px;'+
	'}'+

	'.hmb'+
	'{'+
  '  padding: 8px;'+
	'	 display: none;'+
  '  z-index: 9999;'+
  '  cursor: pointer;'+
	'}'+

	'.content'+
	'{'+
	'	 padding: 0;'+
  '  position: relative;'+
	'}'+

	'.header'+
	'{'+
  '  padding: 12px;'+
  '  cursor: pointer;'+
  '  display: flex;'+
  '  align-items: center;'+
	'}'+

	'.header-icon'+
	'{'+
//  '  float: right;'+
  '  position: absolute;'+
  '  right: 5px;'+
	'}'+

	'.header-text'+
	'{'+
  '  display: inline-block;'+
  '  max-width: 90%;'+
  '  overflow: hidden;'+
	'}'+

	'.hmb-icon'+
	'{'+
  '  float: left;'+
	'}'+

  '.span-content'+
	'{'+
  '  overflow: auto!important;'+
  '  top: 0!important;'+
  '  left: 0!important;'+
  '  width: 100%!important;'+
  '  height: 100%!important;'+
  '  position: absolute!important;'+
  '  white-space: nowrap!important;'+
  '  display: inline-block!important;'+
  '  border-width: 0!important;'+
	'}'+

  '.span-control'+
	'{'+
  '  top: 0!important;'+
  '  left: 0!important;'+
  '  position: relative!important;'+
  '  display: inline-block!important;'+
	'}'+

	'.content-block'+
	'{'+
	'	 height: 350px;'+
	'	 padding: 10px;'+
	'}'+

  '  .state-popup .span-content'+
	'  {'+
  '    position: fixed!important;'+
	'  }'+

	'	 .state-popup .smcontainer'+
	'	 {'+
	'		 display: block;'+
	'	 }'+

	'	 .state-popup .sidebar'+
	'	 {'+
	'		 display: flex;'+
	'		 position: fixed;'+
	'		 top: 40px;'+
	'		 width: 250px;'+
	'		 left: -100%;'+
	'		 height: calc(100vh - 20px);'+
	'	 }'+

	'	 .state-popup .sidebar-open'+
	'	 {'+
	'		 display: flex;'+
	'		 left: 0;'+
	'	 }'+

	'	 .state-popup .header'+
	'	 {'+
	'		 display: none;'+
  '    padding: 8px;' +
	'	 }'+

	'	 .state-popup .hmb'+
	'	 {'+
	'		 position: fixed;'+
  '    display: flex;'+
  '    align-items: center;'+
	'	 }'+

  //Hide details/summary arrow
  '  summary { display: block; }'+
  //Safari support
  '  ::-webkit-details-marker { display: none; }'
  ;
  AddControlStyle(css);
end;

function TCustomSideMenu.CreateElement: TJSElement;
var
  item, html, script: string;
begin

  item := item +
	'			<div class="menu-item">Item Text</div>';

  html := html +
  '<div class="smcontainer smcontainer-open" id="' + Name + 'container">'+

	'<div class="hmb" id="' + Name + 'hmb" onclick="HMBClick()">'+
	'  <span id="' + Name + 'hmb-icn" class="hmb-icon"><i class="material-icons">menu</i></span><span class="hmb-text" id="' + Name +'hmb-txt">WebSideMenu</span>'+
	'</div>'+

  '<div class="sidebar" id="' + Name + 'sidebar">'+
	'	<div class="sidebar-items">'+
	'		<div class="header" id="'+ Name +'hdr" onclick="HeaderClick()">'+
	'			<span class="header-text" id="' + Name + 'hdr-txt">WebSideMenu</span>'+
  '     <span id="' + Name + 'hdr-icn-open" class="header-icon header-icon-open"><i class="material-icons">chevron_left</i></span>'+
  '     <span id="' + Name + 'hdr-icn-close" class="header-icon header-icon-close"><i class="material-icons">chevron_right</i></span>'+
	'		</div>'+
	'		<div class="items-top" id="'+ Name +'nrm">'+
	'		</div>'+
	'		<div class="items-bottom" id="'+ Name +'btm">'+
	'		</div>'+
	'	</div>'+
  '</div>'+
  '<div class="content" id="' + Name + 'content">'+
  Content.Text +
  '</div>'+
  '<div id="' + Name + 'hiddencontent" style="display:none"></div>'+
  '</div>';


  script := script +
  'var ' + Name + 'enableExpandedState = true;'+
  'var ' + Name + 'enableCollapsedState = true;'+
  'var ' + Name + 'enablePopupState = true;'+

  'function UpdateStates(expand, collapse, popup){'+
  '  ' + Name + 'enableExpandedState = expand;'+
  '  ' + Name + 'enableCollapsedState = collapse;'+
  '  ' + Name + 'enablePopupState = popup;'+
  '  toggleState();'+
  '}'+

  'function toggleState(){'+
  '  var sp_container = document.getElementById("' + Name + 'container");'+

  '  if (sp_container){'+
  '    var w1024 = window.matchMedia("(max-width: 1024px)");'+
  '    var w768 = window.matchMedia("(max-width: 768px)");'+

  '    var currentState = "expanded";' +

  '    if (((w768.matches) && (' + Name + 'enablePopupState)) || ((!' + Name + 'enableCollapsedState) && (!' + Name + 'enableExpandedState))) {'+
  '      currentState = "popup";' +
  '    } else if ((w768.matches) && (' + Name + 'enableCollapsedState) && (!' + Name + 'enablePopupState)) {'+
  '      currentState = "collapsed";' +
  '    } else if ( (((w1024.matches) && (!w768.matches)) && (' + Name + 'enableCollapsedState)) || ((!' + Name + 'enablePopupState) && (!' + Name + 'enableExpandedState)) ){'+
  '      currentState = "collapsed";' +
  '    } else if ( (w1024.matches) && (!w768.matches) && (!' + Name + 'enableCollapsedState) && (!' + Name + 'enableExpandedState)){'+
  '      currentState = "popup";' +
  '    } else if ( (!w1024.matches) && (!w768.matches) && (' + Name + 'enableCollapsedState) && (!' + Name + 'enableExpandedState)){'+
  '      currentState = "collapsed";' +
  '    } else if ( (!w1024.matches) && (!w768.matches) && (' + Name + 'enablePopupState) && (!' + Name + 'enableExpandedState)){'+
  '      currentState = "popup";' +
//  '  } else {'+
//  '      currentState = "expanded";' +
  '    }'+

  '    if (currentState == "popup"){'+
  '      sp_container.classList.add("state-popup");'+
  '      sp_container.classList.remove("smcontainer-open");'+
  '      sp_container.classList.remove("smcontainer-close");'+
  '    } else if (currentState == "collapsed"){'+
  '      sp_container.classList.remove("state-popup");'+
  '      sp_container.classList.remove("smcontainer-open");'+
  '      sp_container.classList.add("smcontainer-close");'+
  '    } else {'+
  '      sp_container.classList.remove("state-popup");'+
  '      sp_container.classList.remove("smcontainer-close");'+
  '      sp_container.classList.add("smcontainer-open");'+
  '    }'+
  '  }'+
  '}'+

  'function SetStateClosed(){'+
  '  var sp_sidebar = document.getElementById("' + Name + 'sidebar");'+
  '  if (sp_sidebar){'+
  '    sp_sidebar.classList.remove("sidebar-open");'+
  '  }'+
  '}'+

  'function SetStateOpened(){'+
  '  var sp_sidebar = document.getElementById("' + Name + 'sidebar");'+
  '  if (sp_sidebar){'+
  '    sp_sidebar.classList.add("sidebar-open");'+
  '  }'+
  '}'+

  'function HeaderClick(){'+
  '  var sp_container = document.getElementById("' + Name + 'container");'+
  '  var sp_sidebar = document.getElementById("' + Name + 'sidebar");'+
  '  if (sp_container){'+
  '    sp_container.classList.toggle("smcontainer-open");'+
  '    sp_container.classList.toggle("smcontainer-close");'+
  '  }'+
  '  if (sp_sidebar){'+
  '    sp_sidebar.classList.remove("sidebar-open");'+
  '  }'+
  '}'+

  'function HMBClick(){'+
  '  var sp_sidebar = document.getElementById("' + Name + 'sidebar");'+
  '  if (sp_sidebar){'+
  '    sp_sidebar.classList.toggle("sidebar-open");'+
  '  }'+
  '}'+

  'window.addEventListener("resize", toggleState, true);';

  Result := document.createElement('SPAN');
  FPanelBody := TJSHTMLElement(document.createElement('DIV'));
  FPanelBody.InnerHTML := html;

  Result.appendChild(FPanelBody);
  UpdateCSS;
  AddControlScriptSource(script);
end;

procedure TCustomSideMenu.DoAutoSize;
var
  i: integer;
  minx,miny,maxx,maxy: integer;
  r: TJSDomRect;
  el: TJSElement;
begin
  if FIsSizing then
    Exit;

  FIsSizing := true;

  if (WidthStyle = ssAbsolute) and (HeightStyle = ssAbsolute) then
  begin
    maxx := 0;
    maxy := 0;
    minx := $FFFF;
    miny := $FFFF;
    for i := 0 to ControlCount - 1 do
    begin
      if Controls[i].WidthStyle = ssAbsolute then
      begin
        if Controls[i].Left + Controls[i].Width > maxx then
          maxx := Controls[i].Left + Controls[i].Width;

        if Controls[i].Left < minx then
          minx := Controls[i].Left;
      end
      else
      begin
        el := Controls[i].ElementHandle;
        if Assigned(el) then
        begin
          r := el.getBoundingClientRect;
          if r.X - Left + r.Width > maxx then
            maxx := Round(r.X  - Left + r.Width);

          minx := 0;
        end;
      end;

      if Controls[i].HeightStyle = ssAbsolute then
      begin
        if Controls[i].Top + Controls[i].Height > maxy then
          maxy := Controls[i].Top + Controls[i].Height;

        if Controls[i].Top < miny then
          miny := Controls[i].Top;
      end
      else
      begin
        el := Controls[i].ElementHandle;
        if Assigned(el) then
        begin
          r := el.getBoundingClientRect;
          if r.Y - Top + r.Height > maxy then
            maxy := Round(r.Y - Top + r.Height);
          miny := 0;
        end;
      end;
    end;

    // don't do anything if it would render the panel invisible at design-time
    if (csDesigning in ComponentState) then
    begin
      if (ControlCount = 0) or (maxx = 0) or (maxy = 0) then
        Exit;
    end;

    if (BorderStyle = bsSingle) then
    begin
      inc(maxx);
      inc(maxy);
    end;

    for i := 0 to ControlCount - 1 do
    begin
      Controls[i].Left := Controls[i].Left - minx;
      Controls[i].Top := Controls[i].Top - miny;
    end;

    Width := maxx - minx;
    Height := maxy - miny;
  end
  else
  begin
    Width := -1;
    Height := -1;
  end;

  UpdateElement;

  FIsSizing := false;
end;

procedure TCustomSideMenu.DoContentChanged(Sender: TObject);
var
  el:TJSHTMLElement;
begin
//  UpdateElementVisual;
  if Assigned(ElementHandle) then
  begin
      el := TJSHTMLElement(document.getElementById(GetID+'content'));
      if Assigned(el) then
      begin
        if Assigned(el) then
          el.innerHTML := Content.Text;
      end;
  end;
end;

procedure TCustomSideMenu.DoCSSUpdate(Sender: TObject);
begin
  UpdateCSS;
end;

procedure TCustomSideMenu.DoAppearanceChanged(Sender: TObject);
begin
  UpdateElementVisual;
end;

procedure TCustomSideMenu.DoUpdateList;
var
  el: TJSHTMLElement;
  elitem, elitems: TJSHTMLElement;
  text, img, glyph, badge: TJSHTMLElement;
  ctrl: TJSElement;
  chk: TJSHTMLInputElement;
  I: Integer;
  isexpand: Boolean;

  function RenderSideMenuItem(AItemIndex: Integer; AFixed: Boolean; AID: string; ASubItem: Boolean): TJSHTMLElement;
  var
    it: TSideMenuItem;
    sumel, detel, subel: TJSHTMLElement;
  begin
    Result := elitem;

    elitem := TJSHTMLElement(document.createElement('DIV'));
    elitem.classList.add('menu-item');

    if ASubItem then
      elitem.classList.add('menu-subitem');

    elitem.style.setProperty('display', 'flex');
    elitem.style.setProperty('align-items', 'center');

    isexpand := False;

    if not AFixed then
    begin
      it := Items[AItemIndex];

      elitem.setAttribute('id', AID + IntToStr(AItemIndex));

      if SelectedItem = AItemIndex then
      begin
        elitem.style.setProperty('background-color', ColorToHTML(Appearance.ItemSelectedColor));
        elitem.style.setProperty('color', ColorToHTML(Appearance.ItemSelectedTextColor));

        UpdateContent(it.ContentControl, it.FormClass);
      end;

      if Items.Count > AItemIndex + 1 then
      begin
        if (not it.SubItem) and (Items[AItemIndex + 1].SubItem) then
        begin
          isexpand := True;
        end;
      end;
    end
    else
    begin
      it := FixedItems[AItemIndex];

      if FixedItems.Count > AItemIndex + 1 then
      begin
        if (not it.SubItem) and (FixedItems[AItemIndex + 1].SubItem) then
        begin
          isexpand := True;
        end;
      end;
    end;

    elitem.setAttribute('data', it.Index.ToString);
    elitem.setAttribute('title', it.Hint);

    if it.&Type = itDivider then
    begin
      elitem.classList.add('menu-item-divider');
      elitem.style.setProperty('background-color', ColorToHTML(Appearance.ItemFont.Color));
    end
    else //if it.&Type in [itText, itCheckBox, itTitle, itHTML, itControl] then
    begin

      if isexpand and not (it.&Type in [itHTML, itControl]) then
      begin
        detel := TJSHTMLElement(document.createElement('DETAILS'));
        if it.Expanded then
        begin
          detel.setAttribute('open', 'true');
          glyph := TJSHTMLElement(document.createElement('I'));
          glyph.setAttribute('data', it.Index.ToString);
          glyph.classList.add(GetMaterialClass(it.MaterialGlyphType));
          glyph.classList.add('item-expand-arrow');
          glyph.innerHTML := 'arrow_drop_down';
          glyph.addEventListener('click', @HandleExpandCollapseClick);
          if Appearance.ItemExpandedMaterialGlyph <> '' then
            glyph.innerHTML := Appearance.ItemExpandedMaterialGlyph;
        end
        else
        begin
          glyph := TJSHTMLElement(document.createElement('I'));
          glyph.setAttribute('data', it.Index.ToString);
          glyph.classList.add(GetMaterialClass(it.MaterialGlyphType));
          glyph.classList.add('item-expand-arrow');
          glyph.innerHTML := 'arrow_right';
          glyph.addEventListener('click', @HandleExpandCollapseClick);
          if Appearance.ItemCollapsedMaterialGlyph <> '' then
            glyph.innerHTML := Appearance.ItemCollapsedMaterialGlyph;
        end;
        if (Appearance.ItemExpandGlyphPosition = itpLeft) then
        begin
          elitem.appendChild(glyph);
        end;

        sumel := TJSHTMLElement(document.createElement('SUMMARY'));
        sumel.appendChild(elitem);
        detel.appendChild(sumel);
      end;

      if Appearance.ItemSelectionStyle = itsRounded then
        elitem.classList.add('menu-item-rounded');

      if (it.ImageURLCollapsed <> '') then
      begin
        img := TJSHTMLElement(document.createElement('IMG'));
        img.classList.add('item-image');
        img.classList.add('item-image-large');
        img.setAttribute('src', it.ImageURLCollapsed);
        elitem.appendChild(img);
      end;

      if (it.ImageURL <> '') then
      begin
        if not (it.&Type in [itHTML, itControl]) then
        begin
          img := TJSHTMLElement(document.createElement('IMG'));
          img.classList.add('item-image');
          img.setAttribute('src', it.ImageURL);
          elitem.appendChild(img);
        end;
      end
      else if (it.MaterialGlyph <> '') then
      begin
        glyph := TJSHTMLElement(document.createElement('I'));
        glyph.classList.add(GetMaterialClass(it.MaterialGlyphType));
        if isexpand then
          glyph.classList.add('item-expand');
        if (it.&Type in [itHTML, itControl]) then
          glyph.classList.add('item-control-glyph');
        glyph.innerHTML := it.MaterialGlyph;
        elitem.appendChild(glyph);
      end;

      if (it.&Type = itCheckBox) then
      begin
        chk := TJSHTMLInputElement(document.createElement('INPUT'));
        chk.setAttribute('type', 'checkbox');
        chk.classList.add('item-check');
        chk.checked := it.Checked;
        elitem.appendChild(chk);
      end;

      if (it.&Type = itHTML) then
      begin
        text := TJSHTMLElement(document.createElement('SPAN'));
        text.classList.add('item-text');
        text.innerHTML := it.HTML.Text;
        elitem.appendChild(text);
      end
      else if (it.&Type = itControl) then
      begin
        if Assigned(it.Control) then
        begin
          text := TJSHTMLElement(document.createElement('SPAN'));
          text.classList.add('item-text');
          ctrl := document.getElementById(it.Control.GetID);
          if Assigned(ctrl) then
          begin
            ctrl.classList.add('span-control');
            text.appendChild(ctrl);
          end;
          elitem.appendChild(text);
        end;
      end
      else
      begin
        text := TJSHTMLElement(document.createElement('SPAN'));
        text.classList.add('item-text');
        text.innerHTML := it.Text;
        elitem.appendChild(text);

        if (it.BadgeText <> '') then
        begin
          badge := TJSHTMLElement(document.createElement('SPAN'));
          badge.classList.add('item-badge');
          badge.style.setProperty('background-color', ColorToHTML(it.BadgeColor));
          badge.style.setProperty('color', ColorToHTML(it.BadgeTextColor));
          badge.innerHTML := it.BadgeText;
          elitem.appendChild(badge);
        end;

        if isexpand then
        begin
          if (Appearance.ItemExpandGlyphPosition = itpRight) then
          begin
            elitem.appendChild(glyph);
          end;
        end;
      end;

      if (it.&Type in [itText, itCheckBox, itControl, itHTML]) then
      begin
        if (it.Enabled) then
        begin
          if not AFixed then
          begin
            elitem.addEventListener('click', @HandleItemClick);
            elitem.addEventListener('mouseenter', @HandleItemMouseEnter);
            elitem.addEventListener('mouseleave', @HandleItemMouseLeave);
          end
          else
          begin
            elitem.addEventListener('click', @HandleFixedItemClick);
            elitem.addEventListener('mouseenter', @HandleFixedItemMouseEnter);
            elitem.addEventListener('mouseleave', @HandleFixedItemMouseLeave);
          end;
        end
        else
        begin
          elitem.classList.add('item-disabled');
        end;
      end
      else if (it.&Type in [itTitle, itDivider]) then
      begin
          elitem.classList.add('item-title');
      end;
    end;

    if (not AFixed) and (isexpand) then
    begin
        for I := AItemIndex + 1 to Items.Count - 1 do
        begin
          if Items[I].SubItem then
          begin
            subel := RenderSideMenuItem(I, AFixed, GetID, True);
            detel.appendChild(subel);
          end
          else
          begin
            break;
          end;
        end;

        Result := detel;
    end
    else
    begin
      Result := elitem;
    end;
  end;

begin
  el := TJSHTMLElement(document.getElementById(GetID + 'nrm'));
  if Assigned(el) then
  begin
    elitems := TJSHTMLElement(document.createElement('DIV'));
    elitems.style.setProperty('font', Appearance.ItemFont.ToString);
    elitems.style.setProperty('color', ColorToHTML(Appearance.ItemFont.Color));

    for I := 0 to Items.Count - 1 do
    begin
      if not Items[I].SubItem then
        elitems.appendChild(RenderSideMenuItem(I, False, GetID, False));
    end;

    el.replaceChildren(elitems);
  end;

  el := TJSHTMLElement(document.getElementById(GetID + 'btm'));
  if Assigned(el) then
  begin
    elitems := TJSHTMLElement(document.createElement('DIV'));
    elitems.style.setProperty('font', Appearance.FixedItemFont.ToString);
    elitems.style.setProperty('color', ColorToHTML(Appearance.FixedItemFont.Color));

    for I := 0 to FixedItems.Count - 1 do
    begin
      if not FixedItems[I].SubItem then
        elitems.appendChild(RenderSideMenuItem(I, True, GetID, False));
    end;

    el.replaceChildren(elitems);
  end;
end;

procedure TCustomSideMenu.SetContent(const Value: TStringList);
var
  cnt: TJSHTMLElement;
begin
  if (FContent <> Value) then
  begin
    FContent.Assign(Value);

    if Assigned(ElementHandle) then
    begin
      cnt := TJSHTMLElement(document.getElementById(GetID + 'content'));
      if Assigned(cnt) then
        cnt.innerHTML := Value.Text;
    end;
  end;
end;

procedure TCustomSideMenu.SetContentControl(const Value: TCustomControl);
var
  contentelement, hiddencontentelement, elcontentcontrol: TJSElement;
begin
  if FContentControl <> Value then
  begin
    hiddencontentelement := document.getElementById(GetID + 'hiddencontent');
    contentelement := document.getElementById(GetID + 'content');

    if Assigned(contentelement.firstChild) then
    begin
      TControl.MoveElements(contentelement, hiddencontentelement);
    end;

    { need to unset absolute position properties to ensure aligning of childs remains OK }
    FContentControl := Value;
    FContentControl.ElementPosition := epIgnore;
    FContentControl.WidthStyle := ssAuto;
    FContentControl.HeightStyle := ssAuto;

    if Assigned(FContentControl) then
    begin
      elcontentcontrol := document.getElementById(FContentControl.ElementID);

      if Assigned(elcontentcontrol) then
      begin
        elcontentcontrol.classList.add('span-content');
        contentelement.appendChild(elcontentcontrol);
      end
    end;
//    UpdateElement;
  end;
end;

procedure TCustomSideMenu.SetContentText;
var
  contentelement, hiddencontentelement: TJSElement;
begin
  FContentControl := nil;

  hiddencontentelement := document.getElementById(GetID + 'hiddencontent');
  contentelement := document.getElementById(GetID + 'content');

  if Assigned(contentelement.firstChild) then
  begin
    TControl.MoveElements(contentelement, hiddencontentelement);
  end;

  contentelement.innerHTML := Content.Text;
end;

//procedure TCustomSideMenu.SetParentControl(const Value: TCustomControl);
//var
//  elsidemenucontrol, elcontentcontrol: TJSElement;
//begin
//  if FParentControl <> Value then
//  begin
//    FParentControl := Value;
//
//    if Assigned(FParentControl) then
//    begin
//      elsidemenucontrol := document.getElementById(GetID);
//      elcontentcontrol := document.getElementById(FParentControl.ElementID);
//
//      if Assigned(elcontentcontrol) then
//      begin
//        elcontentcontrol.innerHTML := '';
//        elcontentcontrol.appendChild(elsidemenucontrol);
//        TJSHTMLElement(elcontentcontrol).style.setProperty('display', 'inline-block');
//      end
//    end;
////    UpdateElement;
//  end;
//end;

procedure TCustomSideMenu.SetSelectedItem(const Value: Integer);
begin
  if FSelectedItem <> Value then
  begin
    FSelectedItem := Value;
    UpdateElement;

    if (FSelectedItem >= 0) and (FSelectedItem < Items.Count) then
    begin
      UpdateContent(Items[FSelectedItem].ContentControl, Items[FSelectedItem].FormClass);
    end;
  end;
end;

procedure TCustomSideMenu.SetFixedItems(const Value: TSideMenuItems);
begin
  FFixedItems.Assign(Value);
  UpdateElement;
end;

procedure TCustomSideMenu.SetItems(const Value: TSideMenuItems);
begin
  FItems.Assign(Value);
  UpdateElement;
end;

procedure TCustomSideMenu.SetAlignment(const Value: TAlignment);
begin
  if (FAlignment <> Value) then
  begin
    FAlignment := Value;
    UpdateElement;
  end;
end;

procedure TCustomSideMenu.SetAppearance(const Value: TSideMenuAppearance);
begin
  FAppearance.Assign(Value);
end;

procedure TCustomSideMenu.SetAutoSize(AValue: boolean);
begin
  FAutoSize := AValue;
  if FAutoSize and not IsUpdating then
  begin
    DoAutoSize;
  end;
end;

procedure TCustomSideMenu.SetBorderStyle(const AValue: TBorderStyle);
begin
  inherited;
  UpdateElement;
end;

procedure TCustomSideMenu.SetBounds(X, Y, AWidth, AHeight: Integer);
begin
  inherited;
  if AutoSize then
    DoAutoSize;
end;

procedure TCustomSideMenu.SetPadding(const Value: TPadding);
begin
  FPadding.Assign(Value);
  Realign;
end;

procedure TCustomSideMenu.UnRegisterParent(AValue: TControl);
begin
  inherited;
  if AutoSize then
    DoAutoSize;
end;

procedure TCustomSideMenu.UpdateContent(AContentControl: TCustomControl; AFormClass: TWebFormClass);
begin
  if Assigned(AContentControl) then
  begin
    SetContentControl(AContentControl);
  end
  else if Assigned(AFormClass) then
  begin
    if Assigned(FContentForm) then
      FContentForm.Close;

    FContentForm := AFormClass.CreateNew(ContentElementID, nil);

//    ShowMessage('need to handle form class here');
  end
  else
  begin
    SetContentText;
  end;
end;

{$HINTS OFF}
procedure TCustomSideMenu.UpdateElementVisual;
var
  el:TJSHTMLElement;
  enableExpandedState, enableCollapsedState, enablePopupState: Boolean;
begin
  inherited;

  if Assigned(ElementHandle) then
  begin
    DoUpdateList;

    if not IsLinked then
    begin
      if not IsUpdating then
        AddControlLink('googlematerialall', 'https://fonts.googleapis.com/css?family=Material+Icons|Material+Icons+Outlined|Material+Icons+Round|Material+Icons+Sharp');

      if AutoSize then
      begin
        ElementHandle.style.setProperty('overflow', '');
        ElementHandle.style.setProperty('white-space', 'normal');
        if Visible then
          ElementHandle.style.setProperty('display', 'grid');
      end
      else
      begin
        ElementHandle.style.setProperty('overflow', 'hidden');
        ElementHandle.style.setProperty('white-space', 'nowrap');
        if Visible then
          ElementHandle.style.setProperty('display', 'inline-block');
      end;

      ElementHandle.style.setProperty('padding-left',inttostr(Padding.Left)+'px');
      ElementHandle.style.setProperty('padding-right',inttostr(Padding.Right)+'px');
      ElementHandle.style.setProperty('padding-top',inttostr(Padding.Top)+'px');
      ElementHandle.style.setProperty('padding-bottom',inttostr(Padding.Bottom)+'px');
      ElementHandle.style.setProperty('box-sizing','border-box');

      if ElementBodyClassName <> '' then
        ChildContainer.setAttribute('class', ElementBodyClassName)
      else
        ChildContainer.removeAttribute('class');

      //update states
      enableExpandedState := msExpanded in Appearance.States;
      enableCollapsedState := msCollapsed in Appearance.States;
      enablePopupState := msPopup in Appearance.States;

      asm
        if (typeof UpdateStates == 'function') {
          UpdateStates(enableExpandedState, enableCollapsedState, enablePopupState);
        }
      end;

      el := TJSHTMLElement(document.getElementById(GetID + 'hdr-icn-close'));
      if Assigned(el) then
      begin
        if enableExpandedState then
        begin
          el.style.setProperty('display', '');
        end
        else
        begin
          el.style.setProperty('display', 'none');
        end;
      end;

      el := TJSHTMLElement(document.getElementById(GetID + 'hdr-icn-open'));
      if Assigned(el) then
      begin
        if enableCollapsedState then
        begin
          el.style.setProperty('display', '');
        end
        else
        begin
          el.style.setProperty('display', 'none');
        end;
      end;

      el := TJSHTMLElement(document.getElementById(GetID+'hdr'));
      if Assigned(el) then
      begin
        el.style.setProperty('background-color', ColorToHTML(Appearance.CaptionColor));
        el.style.setProperty('font', Appearance.CaptionFont.ToString);
        el.style.setProperty('color', ColorToHTML(Appearance.CaptionFont.Color));

        if ElementBodyClassName <> '' then
          el.setAttribute('class', 'header ' + ElementCaptionClassName)
        else
          el.setAttribute('class', 'header')
      end;

      el := TJSHTMLElement(document.getElementById(GetID+'hdr-txt'));
      if Assigned(el) then
      begin
        el.innerHTML := Appearance.Caption;
      end;

      el := TJSHTMLElement(document.getElementById(GetID+'hmb-txt'));
      if Assigned(el) then
      begin
        el.innerHTML := Appearance.CaptionPopup;
      end;

      el := TJSHTMLElement(document.getElementById(GetID+'hmb'));
      if Assigned(el) then
      begin
        el.style.setProperty('font', Appearance.CaptionFont.ToString);
        el.style.setProperty('color', ColorToHTML(Appearance.CaptionFont.Color));

        if ElementBodyClassName <> '' then
          el.setAttribute('class', 'hmb ' + ElementCaptionPopupClassName)
        else
          el.setAttribute('class', 'hmb')
      end;

      el := TJSHTMLElement(document.getElementById(GetID+'nrm'));
      if Assigned(el) then
      begin
        el.style.setProperty('background-color', ColorToHTML(Color));

        if ElementBodyClassName <> '' then
          el.setAttribute('class', 'items-top ' + ElementItemsClassName)
        else
          el.setAttribute('class', 'items-top');

        el.scrollTop := FItemsScrollTop;
      end;

      el := TJSHTMLElement(document.getElementById(GetID+'btm'));
      if Assigned(el) then
      begin
        if Appearance.FixedVisible then
          el.style.setProperty('display', 'block')
        else
          el.style.setProperty('display', 'none');

        el.style.setProperty('background-color', ColorToHTML(Appearance.FixedColor));

        if ElementBodyClassName <> '' then
          el.setAttribute('class', 'items-bottom ' + ElementFixedItemsClassName)
        else
          el.setAttribute('class', 'items-bottom');

        el.scrollTop := FFixedItemsScrollTop;
      end;
    end;

      el := TJSHTMLElement(document.getElementById(GetID+'content'));
      if Assigned(el) then
      begin
//        el.innerHTML := Content.Text;

        if ElementBodyClassName <> '' then
          el.setAttribute('class', 'content ' + ElementContentClassName)
        else
          el.setAttribute('class', 'content');
      end;

      if (SelectedItem >= 0) and (SelectedItem < Items.Count) then
      begin
        el := TJSHTMLElement(document.getElementById(GetID + IntToStr(SelectedItem)));
        if Assigned(el) then
        begin
          el.style.setProperty('background-color', ColorToHTML(Appearance.ItemSelectedColor));
          el.style.setProperty('color', ColorToHTML(Appearance.ItemSelectedTextColor));
        end;
      end;

    // do not block selection in child controls
    ElementHandle.style.setProperty('user-select', '');

    // allow default caret on text
    if Cursor = crDefault then
      ElementHandle.style.setProperty('cursor', '');
  end;
end;
{$HINTS ON}

procedure TCustomSideMenu.UpdatePadding(var ARect: TRect);
begin
  ARect.Left := Padding.Left;
  ARect.Top := Padding.Top;
  ARect.Right := ARect.Right - Padding.Right;
  ARect.Bottom := ARect.Bottom - Padding.Bottom;
end;

procedure TCustomSideMenu.UpdateParent;
begin
  inherited;
  DoUpdateList;
end;

{ TSideMenuItem }

procedure TSideMenuItem.Assign(Source: TPersistent);
begin
  if (Source is TSideMenuItem) then
  begin
    FText := (Source as TSideMenuItem).Text;
    FHint := (Source as TSideMenuItem).Hint;
    FEnabled := (Source as TSideMenuItem).Enabled;
    FImageURLCollapsed := (Source as TSideMenuItem).ImageURLCollapsed;
    FImageURL := (Source as TSideMenuItem).ImageURL;
    FType := (Source as TSideMenuItem).&Type;
    FChecked := (Source as TSideMenuItem).Checked;
    FExpanded := (Source as TSideMenuItem).Expanded;
    FHTML.Assign((Source as TSideMenuItem).HTML);
    FContentControl.Assign((Source as TSideMenuItem).ContentControl);
    FControl.Assign((Source as TSideMenuItem).Control);
    FSubItem := (Source as TSideMenuItem).SubItem;
    FMaterialGlyph := (Source as TSideMenuItem).MaterialGlyph;
    FMaterialGlyphType := (Source as TSideMenuItem).MaterialGlyphType;
    FBadgeText := (Source as TSideMenuItem).BadgeText;
    FBadgeColor := (Source as TSideMenuItem).BadgeColor;
    FBadgeTextColor := (Source as TSideMenuItem).BadgeTextColor;
  end;
end;

constructor TSideMenuItem.Create(Collection: TCollection);
begin
  inherited;

  FOwner := (Collection as TSideMenuItems).FOwner;
  FEnabled := true;
  FText := 'Item ' + Collection.Count.ToString;
  FHint := '';
  FImageURLCollapsed := '';
  FImageURL := '';
  FType := itText;
  FChecked := False;
  FExpanded := False;
  FHTML := TStringList.Create;
  FContentControl := nil;
  FControl := nil;
  FSubItem := False;
  FMaterialGlyph := '';
  FMaterialGlyphType := mgNormal;
  FBadgeText := '';
  FBadgeColor := clRed;
  FBadgeTextColor := clWhite;
end;

procedure TSideMenuItem.Click;
begin
  if Assigned(FOnClick) then
    FOnClick(Self);

  if Assigned(FOwner) then
    FOwner.UpdateContent(FContentControl, FFormClass);
end;

destructor TSideMenuItem.Destroy;
begin
  FHTML.Free;
  inherited;
end;

procedure TSideMenuItem.DoChanged;
begin
  if Assigned(Collection) and (Collection is TSideMenuItems) then
    (Collection as TSideMenuItems).DoChanged;
end;

procedure TSideMenuItem.SetBadgeColor(const Value: TColor);
begin
  if FBadgeColor <> Value then
  begin
    FBadgeColor := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetBadgeText(const Value: string);
begin
  if FBadgeText <> Value then
  begin
    FBadgeText := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetBadgeTextColor(const Value: TColor);
begin
  if FBadgeTextColor <> Value then
  begin
    FBadgeTextColor := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetChecked(const Value: Boolean);
begin
  if (FChecked <> Value) then
  begin
    FChecked := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetContentControl(const Value: TCustomControl);
begin
  if (FContentControl <> Value) then
  begin
    FContentControl := Value;

    if not (csDesigning in FOwner.ComponentState) then
      FContentControl.Visible := false;
  end;
end;

procedure TSideMenuItem.SetControl(const Value: TControl);
begin
  if (FControl <> Value) then
  begin
    FControl := Value;

//    if not (csDesigning in FOwner.ComponentState) then
//      FControl.Visible := false;

    DoChanged;
  end;
end;

procedure TSideMenuItem.SetEnabled(const Value: boolean);
begin
  if (FEnabled <> Value) then
  begin
    FEnabled := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetExpanded(const Value: Boolean);
begin
  if FExpanded <> Value then
  begin
    FExpanded := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetHint(const Value: string);
begin
  if (FHint <> Value) then
  begin
    FHint := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetHTML(const Value: TStringList);
begin
  if (FHTML <> Value) then
  begin
    FHTML.Assign(Value);
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetImageURL(const Value: string);
begin
  if (FImageURL <> Value) then
  begin
    FImageURL := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetImageURLCollapsed(const Value: string);
begin
  if (FImageURLCollapsed <> Value) then
  begin
    FImageURLCollapsed := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetMaterialGlyph(const Value: TMaterialGlyph);
begin
  if FMaterialGlyph <> Value then
  begin
    FMaterialGlyph := Value;
    Dochanged;
  end;
end;

procedure TSideMenuItem.SetMaterialGlyphType(const Value: TMaterialGlyphType);
begin
  if FMaterialGlyphType <> Value then
  begin
    FMaterialGlyphType := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetSubItem(const Value: Boolean);
begin
  if FSubItem <> Value then
  begin
    FSubItem := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetText(const Value: string);
begin
  if FText <> Value then
  begin
    FText := Value;
    DoChanged;
  end;
end;

procedure TSideMenuItem.SetType(const Value: TSideMenuItemType);
begin
  if FType <> Value then
  begin
    FType := Value;
    DoChanged;
  end;
end;

{ TSideMenuItems }

function TSideMenuItems.Add: TSideMenuItem;
begin
  Result := TSideMenuItem(inherited Add);
  DoChanged;
end;

constructor TSideMenuItems.Create(AOwner: TPersistent; AItemClass: TCollectionItemClass);
begin
  inherited Create(AOwner, AItemClass);

  if AOwner is TCustomSideMenu then
  begin
    FOwner := AOwner as TCustomSideMenu;
  end;
end;

procedure TSideMenuItems.DoChanged;
begin
  if Assigned(FOwner) then
    FOwner.UpdateParent;
  if Assigned(OnChange) then
    OnChange(Self);
end;

function TSideMenuItems.GetItems(AIndex: integer): TSideMenuItem;
begin
  Result := TSideMenuItem(inherited Items[AIndex]);
end;

function TSideMenuItems.Insert(AIndex: integer): TSideMenuItem;
begin
  Result := TSideMenuItem(inherited Insert(AIndex));
end;

procedure TSideMenuItems.SetItems(AIndex: integer;
  const Value: TSideMenuItem);
begin
  inherited Items[AIndex] := Value;
end;

procedure TSideMenuItems.Update(Item: TCollectionItem);
begin
  inherited;
  DoChanged;
end;

{ TSideMenuAppearance }

procedure TSideMenuAppearance.Assign(Source: TPersistent);
begin
  inherited;
  if (Source is TSideMenuAppearance) then
  begin
    FCaption := (Source as TSideMenuAppearance).Caption;
    FCaptionColor := (Source as TSideMenuAppearance).CaptionColor;
    FCaptionPopup := (Source as TSideMenuAppearance).CaptionPopup;
    FCaptionVisible := (Source as TSideMenuAppearance).CaptionVisible;
    FCaptionFont.Assign((Source as TSideMenuAppearance).CaptionFont);
    FItemFont.Assign((Source as TSideMenuAppearance).ItemFont);
    FItemHoverColor := (Source as TSideMenuAppearance).ItemHoverColor;
    FItemSelectedColor := (Source as TSideMenuAppearance).ItemSelectedColor;
    FItemHoverTextColor := (Source as TSideMenuAppearance).ItemHoverTextColor;
    FItemSelectedTextColor := (Source as TSideMenuAppearance).ItemSelectedTextColor;
    FFixedColor := (Source as TSideMenuAppearance).FixedColor;
    FFixedHeight := (Source as TSideMenuAppearance).FixedHeight;
    FFixedVisible := (Source as TSideMenuAppearance).FixedVisible;
    FFixedItemFont.Assign((Source as TSideMenuAppearance).FixedItemFont);
    FFixedItemHoverColor := (Source as TSideMenuAppearance).FixedItemHoverColor;
    FFixedItemSelectedColor := (Source as TSideMenuAppearance).FixedItemSelectedColor;
    FFixedItemHoverTextColor := (Source as TSideMenuAppearance).FixedItemHoverTextColor;
    FFixedItemSelectedTextColor := (Source as TSideMenuAppearance).FixedItemSelectedTextColor;
    FItemSelectionStyle := (Source as TSideMenuAppearance).ItemSelectionStyle;
    FItemCollapsedMaterialGlyph := (Source as TSideMenuAppearance).ItemCollapsedMaterialGlyph;
    FItemExpandedMaterialGlyph := (Source as TSideMenuAppearance).ItemExpandedMaterialGlyph;
    FItemExpandGlyphPosition := (Source as TSideMenuAppearance).ItemExpandGlyphPosition;
    FStates := (Source as TSideMenuAppearance).States;
  end;
end;

constructor TSideMenuAppearance.Create(AOwner: TCustomSideMenu);
begin
  if Assigned(AOwner) and (AOwner is TCustomSideMenu) then
    FOwner := AOwner;

  FCaption := 'WebSideMenu';
  FCaptionColor := clWebSkyBlue;
  FCaptionPopup := 'WebSideMenu';
  FCaptionVisible := True;
  FCaptionFont := TFont.Create;
  FCaptionFont.Size := 12;
  FCaptionFont.OnChange := DoFontChange;

  FItemFont := TFont.Create;
  FItemFont.Size := 8;
  FItemFont.OnChange := DoFontChange;
  FItemHoverColor := clWebSilver;
  FItemSelectedColor := clWebSkyBlue;
  FItemHoverTextColor := clWebBlack;
  FItemSelectedTextColor := clWebBlack;
  FItemSelectionStyle := itsFull;
  FItemCollapsedMaterialGlyph := '';
  FItemExpandedMaterialGlyph := '';
  FItemExpandGlyphPosition := itpLeft;

  FFixedColor := clWebWhiteSmoke;
  FFixedHeight := 200;
  FFixedVisible := True;

  FFixedItemFont := TFont.Create;
  FFixedItemFont.Size := 8;
  FFixedItemFont.OnChange := DoFontChange;
  FFixedItemHoverColor := clWebSilver;
  FFixedItemSelectedColor := clWebSkyBlue;
  FFixedItemHoverTextColor := clWebBlack;
  FFixedItemSelectedTextColor := clWebBlack;

  FStates := [msCollapsed, msExpanded, msPopup]
end;

destructor TSideMenuAppearance.Destroy;
begin
  FCaptionFont.Free;
  FItemFont.Free;
  FFixedItemFont.Free;
  inherited;
end;

procedure TSideMenuAppearance.DoChange;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TSideMenuAppearance.DoCSS;
begin
  if Assigned(OnCSSUpdate) then
    OnCSSUpdate(Self);
end;

procedure TSideMenuAppearance.DoFontChange(Sender: TObject);
begin
  DoChange;
end;

procedure TSideMenuAppearance.SetCaption(const Value: string);
begin
  if FCaption <> Value then
  begin
    FCaption := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetCaptionPopup(const Value: string);
begin
  if FCaptionPopup <> Value then
  begin
    FCaptionPopup := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetCaptionColor(const Value: TColor);
begin
  if FCaptionColor <> Value then
  begin
    FCaptionColor := Value;
    Dochange;
  end;
end;

procedure TSideMenuAppearance.SetCaptionFont(const Value: TFont);
begin
  if FCaptionFont <> Value then
  begin
    FCaptionFont.Assign(Value);
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetCaptionVisible(const Value: Boolean);
var
  hdr: TJSHTMLElement;
begin
  if FCaptionVisible <> Value then
  begin
    FCaptionVisible := Value;
    Dochange;

    if Assigned(FOwner.ElementHandle) then
    begin
      hdr := TJSHTMLElement(document.getElementById(FOwner.GetID+'hdr'));
      if Assigned(hdr) then
      begin
        if Value then
          hdr.style.setProperty('display','block')
        else
          hdr.style.setProperty('display','none');
      end;
    end;
  end;
end;

procedure TSideMenuAppearance.SetFixedColor(const Value: TColor);
begin
  if FFixedColor <> Value then
  begin
    FFixedColor := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetFixedHeight(const Value: Integer);
begin
  if FFixedHeight <> Value then
  begin
    FFixedHeight := Value;
    DoChange;
    DoCSS;
  end;
end;

procedure TSideMenuAppearance.SetFixedItemFont(const Value: TFont);
begin
  if FFixedItemFont <> Value then
  begin
    FFixedItemFont.Assign(Value);
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetFixedItemHoverColor(const Value: TColor);
begin
  if FFixedItemHoverColor <> Value then
  begin
    FFixedItemHoverColor := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetFixedItemHoverTextColor(const Value: TColor);
begin
  if FFIxedItemHoverColor <> Value then
  begin
    FFixedItemHoverTextColor := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetFixedItemSelectedColor(const Value: TColor);
begin
  if FFixedItemSelectedColor <> Value then
  begin
    FFixedItemSelectedColor := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetFixedItemSelectedTextColor(
  const Value: TColor);
begin
  if FFixedItemSelectedTextColor <> Value then
  begin
    FFixedItemSelectedTextColor := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetFixedVisible(const Value: Boolean);
begin
  if FFixedVisible <> Value then
  begin
    FFixedVisible := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetItemCollapsedMaterialGlyph(
  const Value: TMaterialGlyph);
begin
  if FItemCollapsedMaterialGlyph <> Value then
  begin
    FItemCollapsedMaterialGlyph := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetItemExpandedMaterialGlyph(
  const Value: TMaterialGlyph);
begin
  if FItemExpandedMaterialGlyph <> Value then
  begin
    FItemExpandedMaterialGlyph := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetItemExpandGlyphPosition(
  const Value: TSideMenuItemGlyphPosition);
begin
  if FItemExpandGlyphPosition <> Value then
  begin
    FItemExpandGlyphPosition := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetItemFont(const Value: TFont);
begin
  if FItemFont <> Value then
  begin
    FItemFont.Assign(Value);
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetItemHoverColor(const Value: TColor);
begin
  if FItemHoverColor <> Value then
  begin
    FItemHoverColor := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetItemHoverTextColor(const Value: TColor);
begin
  if FItemHoverTextColor <> Value then
  begin
    FItemHoverTextColor := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetItemSelectedColor(const Value: TColor);
begin
  if FItemSelectedColor <> Value then
  begin
    FItemSelectedColor := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetItemSelectedTextColor(const Value: TColor);
begin
  if FItemSelectedTextColor <> Value then
  begin
    FItemSelectedTextColor := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetItemSelectionStyle(
  const Value: TSideMenuItemSelectionStyle);
begin
  if FItemSelectionStyle <> Value then
  begin
    FItemSelectionStyle := Value;
    DoChange;
  end;
end;

procedure TSideMenuAppearance.SetStates(const Value: TSideMenuStates);
begin
  if FStates <> Value then
  begin
    FStates := Value;
    DoChange;
  end;
end;

end.

